import type { ApiResponse, Ticket } from '@/types/api';
import { TicketStatus, TicketPriority } from '@/types/api';
import { apiClient } from './api';
import { ticketService } from './ticketService';
import { apiEndpoints } from '@/config/app';
import { normalizeApiTimestamp } from '@/utils/ticketTimestamp';

/**
 * The tickets endpoint returns PagedResponseDto ({ data, totalItems, ... }) and
 * serialises TicketStatus/TicketPriority as integers, and it rejects pageSize > 100.
 * ticketService.getTickets already sends the correct PascalCase filter names,
 * unwraps that paged shape and normalises the enums, so dashboard widgets go
 * through it rather than hand-rolling query strings (BugHerd #129).
 */
const MAX_PAGE_SIZE = 100;

/**
 * The shared Ticket type omits ticketNumber, which the API does return and the
 * dashboard displays. Widen locally rather than changing the shared type, which
 * would ripple well beyond this ticket.
 */
type DashboardTicket = Ticket & { ticketNumber?: string };

interface TicketPage {
  tickets: DashboardTicket[];
  totalCount: number;
}

const EMPTY_PAGE: TicketPage = { tickets: [], totalCount: 0 };

async function fetchTicketPage(params: Parameters<typeof ticketService.getTickets>[0]): Promise<TicketPage> {
  const pageSize = Math.min(params?.pageSize ?? MAX_PAGE_SIZE, MAX_PAGE_SIZE);
  const response = await ticketService.getTickets({ ...params, pageSize });

  if (!response.success || !response.data) return EMPTY_PAGE;
  return {
    tickets: response.data.data ?? [],
    totalCount: response.data.totalCount ?? 0,
  };
}

/**
 * The deployed API has no /analytics/agents/{id}/metrics route - unmatched API paths
 * fall through to the SPA shell, so the old call silently yielded zeros. The real
 * route is /analytics/agents/performance, which accepts an agentId filter and returns
 * a list of AgentPerformanceDto. This call stays a local literal because the dashboard
 * only needs a single agent's row; the shared apiEndpoints.analytics.agentPerformance
 * helper now points at the same route and is used by the report page (BugHerd #138).
 */
async function fetchAgentPerformance(agentId: string): Promise<any | null> {
  const response = await apiClient.get(`/analytics/agents/performance?agentId=${encodeURIComponent(agentId)}`);
  if (!response.success) return null;
  const rows = Array.isArray(response.data) ? response.data : [];
  return rows[0] ?? null;
}

/** Statuses that count as "not yet finished" for dashboard widgets. */
const OPEN_STATUSES: TicketStatus[] = [TicketStatus.New, TicketStatus.Open, TicketStatus.Pending];

// Dashboard statistics types
export interface AdminDashboardStats {
  totalTickets: number;
  activeAgents: number;
  openTickets: number;
  avgResolutionTime: string; // formatted time like "4.2h"
  changes?: {
    totalTickets?: { value: string; type: 'increase' | 'decrease' };
    activeAgents?: { value: string; type: 'increase' | 'decrease' };
    openTickets?: { value: string; type: 'increase' | 'decrease' };
    avgResolutionTime?: { value: string; type: 'increase' | 'decrease' };
  };
}

export interface AgentDashboardStats {
  /** Total tickets assigned to this agent. AgentDashboard renders this card. */
  totalTickets: number;
  myOpenTickets: number;
  resolvedToday: number;
  avgResponseTime: string; // formatted time like "2.4h"
  customerRating: string; // formatted rating like "4.8"
  changes?: {
    myOpenTickets?: { value: string; type: 'increase' | 'decrease' };
    resolvedToday?: { value: string; type: 'increase' | 'decrease' };
    avgResponseTime?: { value: string; type: 'increase' | 'decrease' };
    customerRating?: { value: string; type: 'increase' | 'decrease' };
  };
}

export interface EndUserDashboardStats {
  myTickets: number;
  openTickets: number;
  resolvedThisMonth: number;
  avgResolutionTime: string; // formatted time like "6.2h"
  changes?: {
    myTickets?: { value: string; type: 'increase' | 'decrease' };
    resolvedThisMonth?: { value: string; type: 'increase' | 'decrease' };
    avgResolutionTime?: { value: string; type: 'increase' | 'decrease' };
  };
}

export interface DashboardActivity {
  id: string;
  type: 'ticket_created' | 'ticket_updated' | 'ticket_assigned' | 'ticket_resolved' | 'user_login' | 'agent_login';
  title: string;
  description: string;
  timestamp: string;
  user?: {
    name: string;
    role: 'Admin' | 'Agent' | 'EndUser';
  };
}

export interface AssignedTicket {
  id: string;
  ticketNumber: string;
  subject: string;
  priority: TicketPriority;
  status: TicketStatus;
  customer: string;
  lastUpdate: string;
  slaStatus?: 'on-track' | 'at-risk' | 'breached';
  created?: string;
  assignedAgent?: string;
}

export interface QueueStatus {
  urgent: number;
  high: number;
  medium: number;
  low: number;
  totalUnassigned: number;
}

export interface UserTicket {
  id: string;
  ticketNumber: string;
  subject: string;
  priority: TicketPriority;
  status: TicketStatus;
  assignedAgent?: string;
  lastUpdate: string;
  created: string;
}

/**
 * Status of a dependency as reported by the API's health endpoint. Anything the
 * API did not report is 'Unknown' - it must never be presented as Healthy.
 */
export type DependencyStatus = 'Operational' | 'Degraded' | 'Down' | 'Unknown';

export interface SystemHealthStatus {
  apiService: DependencyStatus;
  database: DependencyStatus;
  emailService: DependencyStatus;
  fileStorage: DependencyStatus;
}

export interface UserStatistics {
  activeAgents: number;
  totalAgents: number;
  registeredEndUsers: number;
  totalUsers: number;
  /** Users whose last sign-in falls within the last 24 hours. */
  signedInLast24h: number;
}

export interface AgentPerformanceWeek {
  ticketsResolved: number;
  avgResponseTime: string;
  customerSatisfaction: number;
  satisfactionScale: number;
}

export class DashboardService {
  
  /**
   * Get admin dashboard statistics
   */
  async getAdminStats(): Promise<ApiResponse<AdminDashboardStats>> {
    try {
      // Use analytics endpoint for comprehensive stats
      const executiveResponse = await apiClient.get(apiEndpoints.analytics.executive);
      
      if (executiveResponse.success && executiveResponse.data) {
        const data = executiveResponse.data;

        // Extract KPI values from ExecutiveDashboardDto
        // Prefer explicit KeyMetrics entries if available, otherwise fall back to Summary fields
        const keyMetrics = Array.isArray(data.keyMetrics) ? data.keyMetrics : data.KeyMetrics;
        const summary = data.summary ?? data.Summary ?? {};

        const getMetric = (name: string): number | undefined => {
          if (!keyMetrics) return undefined;
          const m = keyMetrics.find((k: any) => (k.name ?? k.Name) === name);
          return m ? Number(m.value ?? m.Value ?? 0) : undefined;
        };

        const findMetric = (name: string) => {
          if (!keyMetrics) return undefined;
          return keyMetrics.find((k: any) => (k.name ?? k.Name) === name);
        };

        const parseNumber = (value: unknown): number | undefined => {
          if (value === null || value === undefined) {
            return undefined;
          }
          const numeric = typeof value === 'string' ? Number(value) : value;
          return typeof numeric === 'number' && Number.isFinite(numeric) ? numeric : undefined;
        };

        const buildChange = (
          metric: any,
          currentValue?: number
        ): { value: string; type: 'increase' | 'decrease' } | undefined => {
          if (!metric) {
            return undefined;
          }

          const changePercentage = parseNumber(metric.changePercentage ?? metric.ChangePercentage);
          if (changePercentage !== undefined && changePercentage !== 0) {
            return {
              value: `${Math.abs(changePercentage).toFixed(1)}%`,
              type: changePercentage > 0 ? 'increase' : 'decrease'
            };
          }

          const previousValue = parseNumber(metric.previousValue ?? metric.PreviousValue);
          if (
            previousValue !== undefined &&
            currentValue !== undefined &&
            previousValue !== currentValue
          ) {
            const delta = currentValue - previousValue;
            if (delta === 0) {
              return undefined;
            }

            const type: 'increase' | 'decrease' = delta > 0 ? 'increase' : 'decrease';
            if (previousValue === 0) {
              return {
                value: Math.abs(delta).toLocaleString(),
                type
              };
            }

            const percentDelta = Math.abs((delta / previousValue) * 100);
            return {
              value: `${percentDelta.toFixed(1)}%`,
              type
            };
          }

          return undefined;
        };

        const totalTickets = getMetric('total_tickets');
        const openTickets = getMetric('open_tickets');
        const avgResponseHours =
          typeof summary.averageResponseTimeHours === 'number'
            ? summary.averageResponseTimeHours
            : typeof summary.AverageResponseTimeHours === 'number'
            ? summary.AverageResponseTimeHours
            : undefined;
        const activeAgentsCount =
          typeof summary.activeAgentsCount === 'number'
            ? summary.activeAgentsCount
            : typeof summary.ActiveAgentsCount === 'number'
            ? summary.ActiveAgentsCount
            : undefined;

        // Format the data for admin dashboard
        const adminStats: AdminDashboardStats = {
          totalTickets: totalTickets ?? Number(summary.totalTicketsThisMonth ?? summary.TotalTicketsThisMonth ?? 0),
          activeAgents: activeAgentsCount ?? 0,
          openTickets: openTickets ?? 0,
          avgResolutionTime:
            avgResponseHours !== undefined && avgResponseHours > 0 ? `${avgResponseHours.toFixed(1)}h` : 'Not measured',
        };

        const changes: NonNullable<AdminDashboardStats['changes']> = {};

        const totalChange = buildChange(findMetric('total_tickets'), adminStats.totalTickets);
        if (totalChange) {
          changes.totalTickets = totalChange;
        }

        const openChange = buildChange(findMetric('open_tickets'), adminStats.openTickets);
        if (openChange) {
          changes.openTickets = openChange;
        }

        const activeAgentsMetric = findMetric('active_agents') ?? findMetric('active_agents_count');
        const activeChange = buildChange(activeAgentsMetric, adminStats.activeAgents);
        if (activeChange) {
          changes.activeAgents = activeChange;
        }

        const avgMetric =
          findMetric('avg_response_time_hours') ??
          findMetric('average_response_time_hours') ??
          findMetric('avg_resolution_time_hours') ??
          findMetric('average_resolution_time_hours');
        const avgChange = buildChange(avgMetric, avgResponseHours);
        if (avgChange) {
          changes.avgResolutionTime = avgChange;
        }

        if (Object.keys(changes).length > 0) {
          adminStats.changes = changes;
        }

        return {
          success: true,
          data: adminStats
        };
      }
      
      // Fallback to dashboard stats endpoint
      return await apiClient.get<AdminDashboardStats>(apiEndpoints.dashboard.stats);
    } catch (error) {
      console.error('Failed to get admin dashboard stats:', error);
      return {
        success: false,
        error: 'Failed to load dashboard statistics'
      };
    }
  }

  /**
   * Get agent dashboard statistics for current user
   */
  async getAgentStats(userId: string): Promise<ApiResponse<AgentDashboardStats>> {
    try {
      // Get agent specific metrics
      const [performance, assigned] = await Promise.all([
        fetchAgentPerformance(userId),
        // pageSize 1 - only the server-side total is needed for the count card.
        fetchTicketPage({ assignedAgentIds: [userId], pageSize: 1 }),
      ]);
      
      if (performance) {
        const agentStats: AgentDashboardStats = {
          totalTickets: assigned.totalCount,
          myOpenTickets: performance.assignedTickets || 0,
          resolvedToday: performance.resolvedToday || 0,
          // AgentPerformanceDto already reports hours, not minutes.
          avgResponseTime: performance.averageResponseTimeHours ? `${performance.averageResponseTimeHours.toFixed(1)}h` : '0h',
          customerRating: performance.customerSatisfactionScore ? performance.customerSatisfactionScore.toFixed(1) : '0.0'
        };

        return {
          success: true,
          data: agentStats
        };
      }

      // Fallback - return basic structure with zeros
      return {
        success: true,
        data: {
          totalTickets: assigned.totalCount,
          myOpenTickets: 0,
          resolvedToday: 0,
          avgResponseTime: '0h',
          customerRating: '0.0'
        }
      };
    } catch (error) {
      console.error('Failed to get agent dashboard stats:', error);
      return {
        success: false,
        error: 'Failed to load agent statistics'
      };
    }
  }

  /**
   * Get end user dashboard statistics for current user
   */
  async getEndUserStats(userId: string): Promise<ApiResponse<EndUserDashboardStats>> {
    try {
      // Get user's tickets
      // The API scopes EndUser callers to their own tickets server-side.
      const { tickets, totalCount } = await fetchTicketPage({ pageSize: MAX_PAGE_SIZE });

      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const openTickets = tickets.filter((t) => OPEN_STATUSES.includes(t.status)).length;

      const resolvedThisMonth = tickets.filter((t) =>
        t.status === TicketStatus.Resolved && !!t.updatedAt && new Date(t.updatedAt) >= startOfMonth
      ).length;

      // Calculate average resolution time for resolved tickets
      const resolvedTickets = tickets.filter((t) => t.status === TicketStatus.Resolved);
      let avgResolutionTime = '0h';

      if (resolvedTickets.length > 0) {
        const totalTime = resolvedTickets.reduce((acc: number, ticket) => {
          const created = new Date(ticket.createdAt);
          const resolved = new Date(ticket.updatedAt ?? ticket.createdAt);
          return acc + (resolved.getTime() - created.getTime());
        }, 0);

        const avgMs = totalTime / resolvedTickets.length;
        const avgHours = avgMs / (1000 * 60 * 60);
        avgResolutionTime = `${avgHours.toFixed(1)}h`;
      }

      const endUserStats: EndUserDashboardStats = {
        myTickets: totalCount,
        openTickets,
        resolvedThisMonth,
        avgResolutionTime
      };

      return {
        success: true,
        data: endUserStats
      };
    } catch (error) {
      console.error('Failed to get end user dashboard stats:', error);
      return {
        success: false,
        error: 'Failed to load user statistics'
      };
    }
  }

  /**
   * Get recent activities (system-wide for admin, user-specific for others)
   */
  async getRecentActivities(userRole: string, userId?: string): Promise<ApiResponse<DashboardActivity[]>> {
    try {
      // EndUser and Agent activity is derived from their own tickets; only Admin
      // uses the analytics feed. The API scopes ticket queries by caller role.
      if (userRole === 'EndUser' || userRole === 'Agent') {
        const { tickets } = await fetchTicketPage({
          pageSize: 10,
          sortBy: 'UpdatedAt',
          sortDirection: 'desc',
          ...(userRole === 'Agent' && userId ? { assignedAgentIds: [userId] } : {}),
        });

        return {
          success: true,
          data: tickets.slice(0, 5).map((ticket) => ({
            id: ticket.id,
            type: this.getTicketActivityType(ticket.status),
            title: this.formatTicketActivityTitle(ticket),
            description: `${ticket.subject} - ${ticket.ticketNumber ?? ''}`,
            timestamp: this.formatTimestamp(ticket.updatedAt),
          })),
        };
      }

      const response = await apiClient.get('/analytics/realtime-dashboard');
      
      if (response.success) {
        let activities: DashboardActivity[] = [];

        if (userRole === 'Admin' && response.data?.recentActivity) {
          // Transform analytics activity data - use rich backend data directly
          activities = response.data.recentActivity.map((activity: any, index: number) => ({
            id: activity.id || `activity-${index}`,
            type: activity.activityType || activity.type || 'ticket_updated',
            title: activity.description || this.formatActivityTitle(activity.activityType || activity.type, activity),
            description: activity.ticketNumber ? `${activity.ticketNumber}${activity.userName ? ` by ${activity.userName}` : ''}` : (activity.message || 'Activity recorded'),
            timestamp: this.formatTimestamp(activity.timestamp),
            user: activity.userName ? {
              name: activity.userName,
              role: (activity.agentName ? 'Agent' : 'Admin') as const
            } : undefined
          }));
        }

        return {
          success: true,
          data: activities
        };
      }

      return {
        success: true,
        data: []
      };
    } catch (error) {
      console.error('Failed to get recent activities:', error);
      return {
        success: false,
        error: 'Failed to load recent activities'
      };
    }
  }

  /**
   * Get assigned tickets for agent
   */
  async getAssignedTickets(agentId: string): Promise<ApiResponse<AssignedTicket[]>> {
    try {
      const { tickets } = await fetchTicketPage({
        assignedAgentIds: [agentId],
        statuses: OPEN_STATUSES,
        pageSize: 20,
        sortBy: 'UpdatedAt',
        sortDirection: 'desc',
      });

      const assignedTickets: AssignedTicket[] = tickets.map((ticket) => ({
        id: ticket.id,
        ticketNumber: ticket.ticketNumber ?? '',
        subject: ticket.subject,
        priority: ticket.priority,
        status: ticket.status,
        customer: ticket.requester?.fullName || 'Unknown',
        lastUpdate: this.formatTimestamp(ticket.updatedAt),
        slaStatus: this.calculateSlaStatus(ticket)
      }));

      return {
        success: true,
        data: assignedTickets
      };
    } catch (error) {
      console.error('Failed to get assigned tickets:', error);
      return {
        success: false,
        error: 'Failed to load assigned tickets'
      };
    }
  }

  /**
   * Get queue status (unassigned tickets by priority)
   */
  async getQueueStatus(): Promise<ApiResponse<QueueStatus>> {
    try {
      // OnlyUnassigned is applied server-side; totalCount is the exact queue depth
      // even though the priority breakdown is computed from the first page.
      const { tickets, totalCount } = await fetchTicketPage({
        onlyUnassigned: true,
        statuses: OPEN_STATUSES,
        pageSize: MAX_PAGE_SIZE,
      });

      const queueStatus: QueueStatus = {
        urgent: tickets.filter((t) => t.priority === 'Urgent').length,
        high: tickets.filter((t) => t.priority === 'High').length,
        medium: tickets.filter((t) => t.priority === 'Medium').length,
        low: tickets.filter((t) => t.priority === 'Low').length,
        totalUnassigned: totalCount
      };

      return {
        success: true,
        data: queueStatus
      };
    } catch (error) {
      console.error('Failed to get queue status:', error);
      return {
        success: false,
        error: 'Failed to load queue status'
      };
    }
  }

  /**
   * Get user's tickets
   */
  async getUserTickets(userId: string, limit: number = 5): Promise<ApiResponse<UserTicket[]>> {
    try {
      const { tickets } = await fetchTicketPage({
        pageSize: limit,
        sortBy: 'UpdatedAt',
        sortDirection: 'desc',
      });

      const userTickets: UserTicket[] = tickets.map((ticket) => ({
        id: ticket.id,
        ticketNumber: ticket.ticketNumber ?? '',
        subject: ticket.subject,
        priority: ticket.priority,
        status: ticket.status,
        assignedAgent: ticket.assignedTo?.fullName || undefined,
        lastUpdate: this.formatTimestamp(ticket.updatedAt),
        created: this.formatTimestamp(ticket.createdAt)
      }));

      return {
        success: true,
        data: userTickets
      };
    } catch (error) {
      console.error('Failed to get user tickets:', error);
      return {
        success: false,
        error: 'Failed to load tickets'
      };
    }
  }

  /**
   * Get system health status
   */
  async getSystemHealth(): Promise<ApiResponse<SystemHealthStatus>> {
    // /health/detailed is the only health endpoint that reports the email and
    // storage dependencies; the plain /health check covers the database only.
    try {
      const response = await apiClient.get('/health/detailed', {
        // A failed dependency still returns usable health observations. Accept it
        // here so the shared error normalizer does not discard that payload.
        validateStatus: status => status === 200 || status === 503,
      });

      if (response.success && response.data && typeof response.data === 'object') {
        return {
          success: true,
          data: this.buildSystemHealth(response.data)
        };
      }
    } catch (error) {
      console.error('Failed to get system health:', error);
    }

    // Nothing is known about any dependency. Report that, rather than guessing
    // a healthy status.
    return {
      success: true,
      data: {
        apiService: 'Unknown',
        database: 'Unknown',
        emailService: 'Unknown',
        fileStorage: 'Unknown'
      }
    };
  }

  /**
   * Get user statistics
   */
  async getUserStatistics(): Promise<ApiResponse<UserStatistics | null>> {
    try {
      const response = await apiClient.get(apiEndpoints.users.list + '?pageSize=1000');

      if (response.success && Array.isArray(response.data?.data)) {
        const users = [...response.data.data];
        let page = 1;
        let body = response.data;
        while (body.hasNextPage || (typeof body.totalCount === 'number' && users.length < body.totalCount)) {
          const next = await apiClient.get(`${apiEndpoints.users.list}?page=${++page}&pageSize=1000`);
          if (!next.success || !Array.isArray(next.data?.data) || next.data.data.length === 0)
            throw new Error('The complete user list is unavailable');
          body = next.data;
          users.push(...body.data);
        }
        const agents = users.filter((u: any) => u.role === 'Agent');
        const endUsers = users.filter((u: any) => u.role === 'EndUser');

        // Count users whose last sign-in falls within the last 24 hours. The API
        // does not expose a live session concept, so this sign-in recency count is
        // what the "active" figure reports - and it is labelled that way in the UI.
        const now = new Date();
        const recentThreshold = new Date(now.getTime() - 24 * 60 * 60 * 1000); // 24 hours ago
        const signedInLast24h = users.filter((u: any) => {
          const timestamp = normalizeApiTimestamp(u.lastLoginAt);
          if (!timestamp) return false;
          const lastLogin = new Date(timestamp);
          return lastLogin >= recentThreshold && lastLogin <= now;
        }).length;

        const activeAgentsCount = agents.filter((a: any) => a.status === 'Active' || a.isActive !== false).length;

        const userStats: UserStatistics = {
          activeAgents: activeAgentsCount,
          totalAgents: agents.length,
          registeredEndUsers: endUsers.length,
          totalUsers: typeof body.totalCount === 'number' ? body.totalCount : users.length,
          signedInLast24h
        };

        return {
          success: true,
          data: userStats
        };
      }

      throw new Error('User statistics response is unavailable');
    } catch (error) {
      console.error('❌ [getUserStatistics] Failed to get user statistics:', error);
      return {
        success: false,
        data: null,
        message: 'Failed to load user statistics'
      };
    }
  }

  /**
   * Get agent performance for current week
   */
  async getAgentPerformanceWeek(agentId: string): Promise<ApiResponse<AgentPerformanceWeek>> {
    try {
      const metrics = await fetchAgentPerformance(agentId);
      
      if (metrics) {
        // Only report what AgentPerformanceDto actually provides. The old goal
        // figures (35 tickets, 4h response) were invented frontend constants.
        const performance: AgentPerformanceWeek = {
          ticketsResolved: metrics.resolvedThisWeek || 0,
          // AgentPerformanceDto already reports hours, not minutes.
          avgResponseTime: metrics.averageResponseTimeHours ? `${metrics.averageResponseTimeHours.toFixed(1)}h` : '0h',
          customerSatisfaction: metrics.customerSatisfactionScore || 0,
          satisfactionScale: 5
        };

        return {
          success: true,
          data: performance
        };
      }

      return {
        success: true,
        data: {
          ticketsResolved: 0,
          avgResponseTime: '0h',
          customerSatisfaction: 0,
          satisfactionScale: 5
        }
      };
    } catch (error) {
      console.error('Failed to get agent performance:', error);
      return {
        success: false,
        error: 'Failed to load performance data'
      };
    }
  }

  private normalizeDependencies(raw: unknown): Record<string, any> {
    if (!raw) {
      return {};
    }

    if (raw instanceof Map) {
      const normalized: Record<string, any> = {};
      raw.forEach((value, key) => {
        normalized[String(key).toLowerCase()] = value;
      });
      return normalized;
    }

    if (typeof raw === 'object') {
      return Object.entries(raw as Record<string, any>).reduce<Record<string, any>>((acc, [key, value]) => {
        acc[key.toLowerCase()] = value;
        return acc;
      }, {});
    }

    return {};
  }

  private normalizeHealthStatus(value: unknown): 'healthy' | 'degraded' | 'unhealthy' | null {
    if (!value) {
      return null;
    }

    const status = String(value).trim().toLowerCase();
    if (['healthy', 'operational', 'up', 'ok', 'passed'].includes(status)) {
      return 'healthy';
    }
    if (['degraded', 'warning', 'unstable', 'partial'].includes(status)) {
      return 'degraded';
    }
    if (['unhealthy', 'down', 'failed', 'critical', 'error'].includes(status)) {
      return 'unhealthy';
    }
    return null;
  }

  /**
   * Map the API health payload to display statuses using only what the API
   * actually reported. A dependency the payload does not mention stays Unknown.
   */
  private buildSystemHealth(health: Record<string, any>): SystemHealthStatus {
    const dependencies = this.normalizeDependencies(health.dependencies ?? health.Dependencies);

    return {
      // The API answered the request, so the service itself is up.
      apiService: 'Operational',
      database: this.toDependencyStatus(dependencies['database']),
      emailService: this.toDependencyStatus(dependencies['emailservice']),
      fileStorage: this.toDependencyStatus(dependencies['filesystem'])
    };
  }

  private toDependencyStatus(...sources: Array<unknown>): SystemHealthStatus['database'] {
    for (const source of sources) {
      if (!source) {
        continue;
      }
      const statusValue = typeof source === 'string' ? source : (source as any).status ?? (source as any).Status;
      const normalized = this.normalizeHealthStatus(statusValue);
      if (normalized === 'healthy') {
        return 'Operational';
      }
      if (normalized === 'degraded') {
        return 'Degraded';
      }
      if (normalized === 'unhealthy') {
        return 'Down';
      }
    }

    return 'Unknown';
  }
  // Helper methods
  private formatActivityTitle(type: string, activity: any): string {
    switch (type) {
      case 'ticket_created':
        return activity?.ticketNumber ? `New ticket ${activity.ticketNumber} created` : 'New ticket created';
      case 'ticket_updated':
        return activity?.ticketNumber ? `Ticket ${activity.ticketNumber} updated` : 'Ticket updated';
      case 'ticket_resolved':
        return activity?.ticketNumber ? `Ticket ${activity.ticketNumber} resolved` : 'Ticket resolved';
      case 'conversation_added':
        return activity?.ticketNumber ? `New response on ${activity.ticketNumber}` : 'New response added';
      case 'ticket_assigned':
        return activity?.ticketNumber ? `Ticket ${activity.ticketNumber} assigned` : 'Ticket assigned';
      case 'agent_login':
        return 'Agent logged in';
      case 'agent_logout':
        return 'Agent logged out';
      case 'user_login':
        return 'User logged in';
      default:
        // Fallback to the backend-provided description if available
        return activity?.description || 'System activity';
    }
  }

  private formatTicketActivityTitle(ticket: any): string {
    switch (ticket.status) {
      case 'Resolved':
        return 'Ticket resolved';
      case 'InProgress':
        return 'Ticket in progress';
      case 'Open':
        return 'Ticket opened';
      default:
        return 'Ticket updated';
    }
  }

  private getTicketActivityType(status: string): DashboardActivity['type'] {
    switch (status) {
      case 'Resolved':
        return 'ticket_resolved';
      case 'InProgress':
        return 'ticket_updated';
      case 'Open':
        return 'ticket_created';
      default:
        return 'ticket_updated';
    }
  }

  private formatTimestamp(timestamp: string | null | undefined): string {
    if (!timestamp) return 'Unknown time';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      return `${diffMins} minutes ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  }

  private calculateSlaStatus(ticket: any): 'on-track' | 'at-risk' | 'breached' {
    // This is a simplified SLA calculation
    // In reality, this should use the SLA service
    const created = new Date(ticket.createdAt);
    const now = new Date();
    const ageHours = (now.getTime() - created.getTime()) / (1000 * 60 * 60);
    
    // Basic SLA thresholds by priority
    const slaHours = {
      'Urgent': 4,
      'High': 8,
      'Medium': 24,
      'Low': 72
    };
    
    const threshold = slaHours[ticket.priority as keyof typeof slaHours] || 24;
    
    if (ageHours > threshold) {
      return 'breached';
    } else if (ageHours > threshold * 0.8) {
      return 'at-risk';
    } else {
      return 'on-track';
    }
  }
}

// Create and export singleton instance
export const dashboardService = new DashboardService();
